import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, Upload, Loader, Sparkles, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const RealisationFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    city: '',
    postal_code: '',
    department: '',
    region: '',
    date: new Date().getFullYear().toString(),
    category: 'Ponts de quai',
    service_type: 'Installation neuve',
    brand: '',
    equipments: [],
    main_image: '',
    gallery_images: [],
    before_images: [],
    after_images: [],
    problem_description: '',
    solution_description: '',
    works_description: '',
    equipments_details: '',
    constraints: '',
    result: '',
    h1: '',
    intro: '',
    context: '',
    problem: '',
    solution: '',
    works: '',
    result_text: '',
    conclusion: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    indexable: true,
    status: 'draft'
  });
  
  const [newEquipment, setNewEquipment] = useState('');
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState('');

  const categories = [
    'Ponts de quai',
    'Niveleurs hydrauliques',
    'Mini niveleurs',
    'Butoirs',
    'Garde-corps et barrières',
    'Installation complète'
  ];

  const serviceTypes = [
    'Installation neuve',
    'Rénovation',
    'Maintenance',
    'Réparation',
    'Mise aux normes',
    'Remplacement',
    'Modernisation'
  ];

  const regions = [
    "Auvergne-Rhône-Alpes",
    "Bourgogne-Franche-Comté",
    "Bretagne",
    "Centre-Val de Loire",
    "Corse",
    "Grand Est",
    "Hauts-de-France",
    "Île-de-France",
    "Normandie",
    "Nouvelle-Aquitaine",
    "Occitanie",
    "Pays de la Loire",
    "Provence-Alpes-Côte d'Azur"
  ];

  // Auto-détection région/département depuis code postal
  const handlePostalCodeChange = async (e) => {
    const postalCode = e.target.value;
    setFormData(prev => ({ ...prev, postal_code: postalCode }));

    if (postalCode.length >= 2) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/geo/detect?postal_code=${postalCode}`);
        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            department: data.department || prev.department,
            region: data.region || prev.region
          }));
        }
      } catch (error) {
        console.error('Erreur détection géo:', error);
      }
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchRealisation();
    }
  }, [id]);

  const fetchRealisation = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/realisations/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(data);
        setMainImagePreview(data.main_image);
      } else {
        toast.error('Réalisation non trouvée');
        navigate('/admin/realisations');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddEquipment = () => {
    if (newEquipment.trim()) {
      setFormData(prev => ({
        ...prev,
        equipments: [...prev.equipments, newEquipment.trim()]
      }));
      setNewEquipment('');
    }
  };

  const handleRemoveEquipment = (index) => {
    setFormData(prev => ({
      ...prev,
      equipments: prev.equipments.filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToCloudinary = async (file) => {
    const token = localStorage.getItem('admin_token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', 'Realisation Image');
    formData.append('location', 'Upload');
    formData.append('type', 'Realisation');
    formData.append('year', new Date().getFullYear().toString());
    formData.append('description', 'Image de réalisation');

    const response = await fetch(`${BACKEND_URL}/api/gallery/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    if (!response.ok) {
      throw new Error('Échec upload Cloudinary');
    }

    const data = await response.json();
    return data.image_url;
  };

  const handleGenerateContent = async () => {
    if (!formData.title || !formData.city || !formData.category) {
      toast.error('Remplissez au moins le titre, la ville et la catégorie');
      return;
    }

    try {
      setGenerating(true);
      const token = localStorage.getItem('admin_token');
      
      // Créer d'abord la réalisation si nouveau
      let realisationId = id;
      if (!isEdit) {
        const createResponse = await fetch(`${BACKEND_URL}/api/realisations`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            status: 'draft'
          })
        });
        
        if (!createResponse.ok) throw new Error('Création échouée');
        const createData = await createResponse.json();
        realisationId = createData.id;
      }

      // Générer le contenu
      const response = await fetch(`${BACKEND_URL}/api/realisations/${realisationId}/generate-content`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Génération échouée');
      
      const data = await response.json();
      const generated = data.generated_content;

      setFormData(prev => ({
        ...prev,
        h1: generated.h1 || prev.h1,
        intro: generated.intro || prev.intro,
        context: generated.context || prev.context,
        solution: generated.solution || prev.solution,
        seo_title: generated.seo_title || prev.seo_title,
        seo_description: generated.seo_description || prev.seo_description
      }));

      toast.success('Contenu généré avec succès ! Vous pouvez le modifier.');
    } catch (error) {
      console.error('Erreur génération:', error);
      toast.error('Erreur lors de la génération IA');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.city || !formData.category) {
      toast.error('Titre, ville et catégorie sont obligatoires');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');

      // Upload image si nouvelle
      let imageUrl = formData.main_image;
      if (mainImageFile) {
        setUploading(true);
        imageUrl = await uploadImageToCloudinary(mainImageFile);
        setUploading(false);
      }

      const dataToSend = {
        ...formData,
        main_image: imageUrl
      };

      const url = isEdit 
        ? `${BACKEND_URL}/api/realisations/${id}`
        : `${BACKEND_URL}/api/realisations`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        throw new Error('Échec de sauvegarde');
      }

      toast.success(isEdit ? 'Réalisation mise à jour' : 'Réalisation créée');
      navigate('/admin/realisations');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-[#FF6600]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#020d2a] text-white py-6 sticky top-0 z-10 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">
              {isEdit ? 'Modifier la réalisation' : 'Nouvelle réalisation'}
            </h1>
            <button
              onClick={() => navigate('/admin/realisations')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Annuler
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="container mx-auto px-4 py-8 max-w-4xl">
        
        {/* Informations de base */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-[#020d2a] mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Informations de base
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre de la réalisation *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                placeholder="Ex: Pont aluminium 820 + Chassis complet"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ville *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handlePostalCodeChange}
                placeholder="Ex: 21000"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Le département et la région seront détectés automatiquement</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                placeholder="Ex: 21 - Côte-d'Or"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Région</label>
              <select
                name="region"
                value={formData.region}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              >
                <option value="">Sélectionner...</option>
                {regions.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type de prestation</label>
              <select
                name="service_type"
                value={formData.service_type}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              >
                {serviceTypes.map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marque (si connue)
              </label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                placeholder="Ex: Stertil, Haacon... (laisser vide si inconnu)"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">⚠️ Ne jamais inventer une marque</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Année/Date</label>
              <input
                type="text"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut de publication</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              >
                <option value="draft">📝 Brouillon (non visible publiquement)</option>
                <option value="published">✅ Publiée (visible sur le site)</option>
                <option value="archived">📦 Archivée (masquée)</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="indexable"
                  checked={formData.indexable}
                  onChange={(e) => setFormData(prev => ({ ...prev, indexable: e.target.checked }))}
                  className="w-5 h-5 text-[#FF6600] border-gray-300 rounded focus:ring-[#FF6600]"
                />
                <span className="text-sm font-medium text-gray-700">
                  🔍 Autoriser l'indexation Google (SEO)
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-7">
                Décochez pour ajouter une balise noindex (page non référencée)
              </p>
            </div>
          </div>
        </div>

        {/* Image principale */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-[#020d2a] mb-4">Image principale</h2>
          
          <div className="flex gap-6">
            {mainImagePreview && (
              <div className="w-64 h-48 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={mainImagePreview}
                  alt="Aperçu"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div className="flex-1">
              <label className="block cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#FF6600] transition-colors">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">Cliquez pour uploader une image</p>
                  <p className="text-sm text-gray-500">Format JPG, PNG, WebP</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Équipements */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-[#020d2a] mb-4">Équipements installés</h2>
          
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newEquipment}
              onChange={(e) => setNewEquipment(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEquipment())}
              placeholder="Ex: Pont de quai aluminium"
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
            />
            <button
              type="button"
              onClick={handleAddEquipment}
              className="px-6 py-2 bg-[#FF6600] text-white rounded-lg hover:bg-[#e55a00] transition-colors"
            >
              Ajouter
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {formData.equipments.map((eq, idx) => (
              <span
                key={idx}
                className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
              >
                {eq}
                <button
                  type="button"
                  onClick={() => handleRemoveEquipment(idx)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Détails des équipements (optionnel)
            </label>
            <textarea
              name="equipments_details"
              value={formData.equipments_details}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
              placeholder="Détails techniques, caractéristiques..."
            />
          </div>
        </div>

        {/* Descriptions utilisateur */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-[#020d2a] mb-4">Description du chantier</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Problème/Besoin initial *
              </label>
              <textarea
                name="problem_description"
                value={formData.problem_description}
                onChange={handleInputChange}
                rows={3}
                required
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                placeholder="Quel était le problème ou le besoin du client ?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Solution mise en place *
              </label>
              <textarea
                name="solution_description"
                value={formData.solution_description}
                onChange={handleInputChange}
                rows={4}
                required
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                placeholder="Décrivez brièvement la solution Orion Industrie..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Travaux réalisés en détail
              </label>
              <textarea
                name="works_description"
                value={formData.works_description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                placeholder="Détails des travaux : dépose, installation, raccordement, mise en service..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Résultat / Satisfaction client
              </label>
              <textarea
                name="result"
                value={formData.result}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                placeholder="Quel a été le résultat ? Le client est-il satisfait ?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraintes (optionnel)
              </label>
              <textarea
                name="constraints"
                value={formData.constraints}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                placeholder="Contraintes techniques, délais..."
              />
            </div>
          </div>
        </div>

        {/* Génération IA */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg shadow-md p-6 mb-6 border border-purple-200">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-bold text-[#020d2a] mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Génération automatique du contenu SEO
              </h2>
              <p className="text-sm text-gray-600">
                L'IA va générer un H1, une introduction, du contexte et des descriptions SEO optimisées.
              </p>
            </div>
            <button
              type="button"
              onClick={handleGenerateContent}
              disabled={generating || !formData.title || !formData.city}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
            >
              {generating ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Générer avec IA
                </>
              )}
            </button>
          </div>

          {/* Champs générés (éditables) */}
          <div className="space-y-4 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">H1 SEO</label>
              <input
                type="text"
                name="h1"
                value={formData.h1}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                placeholder="Sera généré par l'IA"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Introduction</label>
              <textarea
                name="intro"
                value={formData.intro}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contexte du chantier</label>
              <textarea
                name="context"
                value={formData.context}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">La solution Orion Industrie</label>
              <textarea
                name="solution"
                value={formData.solution}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Title (max 60 car.)
                </label>
                <input
                  type="text"
                  name="seo_title"
                  value={formData.seo_title}
                  onChange={handleInputChange}
                  maxLength={60}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                />
                <span className="text-xs text-gray-500">{formData.seo_title.length}/60</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Description (max 160 car.)
                </label>
                <textarea
                  name="seo_description"
                  value={formData.seo_description}
                  onChange={handleInputChange}
                  maxLength={160}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                />
                <span className="text-xs text-gray-500">{formData.seo_description.length}/160</span>
              </div>
            </div>
          </div>
        </div>

        {/* Résultat */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-[#020d2a] mb-4">Résultat final (optionnel)</h2>
          <textarea
            name="result_text"
            value={formData.result_text}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
            placeholder="Quel a été le résultat ? Le client est-il satisfait ?"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end sticky bottom-4 bg-white p-4 rounded-lg shadow-lg border">
          <button
            type="button"
            onClick={() => navigate('/admin/realisations')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading || uploading}
            className="flex items-center gap-2 px-8 py-3 bg-[#FF6600] text-white rounded-lg hover:bg-[#e55a00] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg font-semibold"
          >
            {uploading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Upload en cours...
              </>
            ) : loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isEdit ? 'Mettre à jour' : 'Créer la réalisation'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RealisationFormPage;
